import {
  useState,
  useEffect,
  RefObject,
  Dispatch,
  SetStateAction
} from 'react';
import Konva from 'konva';
import { Image, useSlideEditorContext } from 'providers/SlideEditorProvider';
import { SET_DELETE_MODAL } from 'reducers/SlideEditorReducer';
import { ImageData } from 'pages/SlideEditor/SlideTemplateEditor';
import { uploadImageAndGetUrl } from 'services/uploadImage';

interface IProps {
  layerRef: RefObject<Konva.Layer>;
  isText: boolean;
  setLocationImageNode: (location: { x: number; y: number }) => void;
  setIsText: Dispatch<SetStateAction<boolean>>;
  imagesRef: React.MutableRefObject<ImageData[]>;
  setShowModalStyle: Dispatch<SetStateAction<boolean>>;
  setTextActive: Dispatch<SetStateAction<number>>;
  transformerListRef: React.MutableRefObject<Konva.Transformer[]>;
}

const useImageEditor = ({
  layerRef,
  isText,
  setLocationImageNode,
  setIsText,
  imagesRef,
  setShowModalStyle,
  setTextActive,
  transformerListRef
}: IProps) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [imageActive, setImageActive] = useState<number | null>(null);
  const [transformerImageList, setTransformerImageList] = useState<
    Konva.Transformer[]
  >([]);
  const { slideEditorDispatch } = useSlideEditorContext();

  const setLocation = (konvaImage: Konva.Image) => {
    const imagePosition = konvaImage.absolutePosition();
    const stage = konvaImage.getStage();
    const stageContainerRect = stage
      ? stage.container().getBoundingClientRect()
      : null;

    if (!stageContainerRect) return;

    const areaPosition = {
      x: stageContainerRect.left + imagePosition.x,
      y: stageContainerRect.top + imagePosition.y
    };
    setLocationImageNode({ x: areaPosition.x, y: areaPosition.y });
  };

  const listener = (konvaImage: Konva.Image) => {
    konvaImage.on('click tap', () => {
      transformerListRef.current.forEach(transformer => {
        transformer.hide();
      });
      setIsText(false);
      setTextActive(-1);

      const index = imagesRef.current.findIndex(
        img => img.konvaImage === konvaImage
      );
      setImageActive(prevIndex => (prevIndex === index ? null : index));
      setLocation(konvaImage);
    });

    konvaImage.on('dragstart', () => {
      setShowModalStyle(false);
      const index = imagesRef.current.findIndex(
        img => img.konvaImage === konvaImage
      );

      setImageActive(prevIndex =>
        prevIndex !== null && prevIndex >= 0 ? index : null
      );
    });

    konvaImage.on('dragmove', () => {
      setLocation(konvaImage);
    });

    konvaImage.on('transform', () => {
      konvaImage.setAttrs({
        width: konvaImage.width() * konvaImage.scaleX(),
        height: konvaImage.height() * konvaImage.scaleY(),
        scaleX: 1,
        scaleY: 1
      });
      slideEditorDispatch({
        type: SET_DELETE_MODAL,
        payload: false
      });
    });
  };

  const handleUploadImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError('');

    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    setUploadingImage(true);
    const file = event.target.files[0];

    try {
      const url = await uploadImageAndGetUrl(file);

      if (url) {
        const image = new window.Image();
        image.src = url;
        image.onload = () => {
          const fixedWidth = 300;
          const aspectRatio = image.width / image.height;
          const fixedHeight = fixedWidth / aspectRatio;

          const konvaImage = new Konva.Image({
            x: 50,
            y: 50,
            image: image,
            width: fixedWidth,
            height: fixedHeight,
            draggable: true
          });

          listener(konvaImage);

          imagesRef.current.push({ konvaImage, url });

          if (layerRef.current) {
            layerRef.current.add(konvaImage);
            layerRef.current.draw();
          }
        };
      } else {
        setError('Upload failed. Please try again.');
      }
    } catch (error) {
      setError(
        'Upload failed. Please check your network connection and try again.'
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const addImageOnSlide = async (imageData: Image) => {
    return new Promise<void>(resolve => {
      const image = new window.Image();
      image.src = imageData.url;
      image.onload = () => {
        const aspectRatio = image.width / image.height;
        const fixedHeight = imageData.width / aspectRatio;

        const konvaImage = new Konva.Image({
          x: imageData.x,
          y: imageData.y,
          image: image,
          width: imageData.width,
          height: fixedHeight,
          rotation: imageData.roration,
          draggable: true
        });

        listener(konvaImage);

        imagesRef.current.push({ konvaImage, url: imageData.url });

        if (layerRef.current) {
          layerRef.current.add(konvaImage);
          layerRef.current.draw();
        }
        resolve();
      };
    });
  };

  useEffect(() => {
    const stage = layerRef.current?.getStage();
    if (!stage) return;

    const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (imageActive !== null && e.target === stage) {
        setImageActive(null);
      }
    };

    stage.on('click tap', handleClick);

    return () => {
      stage.off('click tap', handleClick);
    };
  }, [imageActive, layerRef]);

  useEffect(() => {
    if (layerRef.current) {
      const transformer = new Konva.Transformer();
      layerRef.current.add(transformer);

      if (imageActive !== null && imagesRef.current[imageActive]) {
        transformer.attachTo(imagesRef.current[imageActive].konvaImage);
        setTransformerImageList(prev => [...prev, transformer]);
        slideEditorDispatch({
          type: SET_DELETE_MODAL,
          payload: true
        });
        setIsText(false);
      } else if (!isText) {
        slideEditorDispatch({
          type: SET_DELETE_MODAL,
          payload: false
        });
        setIsText(true);
        transformer.detach();
      }

      layerRef.current.draw();

      return () => {
        transformer.detach();
        transformer.destroy();
        layerRef.current?.draw();
      };
    }
  }, [imageActive, imagesRef, layerRef]);

  const handleClearImages = () => {
    if (layerRef.current) {
      const images = layerRef.current.find('Image');
      images.forEach(imageNode => {
        imageNode.destroy();
      });

      transformerImageList.forEach(transformer => {
        transformer.detach();
        transformer.destroy();
      });

      imagesRef.current = [];
      setImageActive(null);
      setTransformerImageList([]);
      layerRef.current.draw();
    }
  };

  const handleDeleteActiveImage = () => {
    if (
      imageActive !== null &&
      imagesRef.current[imageActive] &&
      layerRef.current
    ) {
      imagesRef.current[imageActive].konvaImage.destroy();
      transformerImageList.forEach(transformer => {
        transformer.detach();
        transformer.destroy();
      });
      setImageActive(null);
      setTransformerImageList([]);
      layerRef.current.draw();
    }
  };

  return {
    handleUploadImage,
    handleClearImages,
    handleDeleteActiveImage,
    setImageActive,
    uploadingImage,
    error,
    imagesRef,
    addImageOnSlide,
    imageActive
  };
};

export default useImageEditor;
